import { ReactNode } from 'react';

type BadgeVariant = 'default' | 'primary' | 'success' | 'warning' | 'danger';

interface BadgeProps {
  children: ReactNode;
  variant?: BadgeVariant;
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * Badge component for labels and status indicators.
 */
export function Badge({
  children,
  variant = 'default',
  size = 'sm',
  className = '',
}: BadgeProps) {
  const variantClasses = {
    default: 'bg-white/10 border border-border text-gray-300',
    primary: 'bg-accent/20 border border-accent/30 text-accent',
    success: 'bg-success/20 border border-success/30 text-success',
    warning: 'bg-amber-500/20 border border-amber-500/30 text-amber-400',
    danger: 'bg-danger/20 border border-danger/30 text-danger',
  };

  const sizeClasses = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
  };

  return (
    <span
      className={`
        inline-flex items-center gap-1
        rounded-full font-medium
        ${variantClasses[variant]}
        ${sizeClasses[size]}
        ${className}
      `}
    >
      {children}
    </span>
  );
}

interface StatusBadgeProps {
  status: 'online' | 'offline' | 'away' | 'busy';
  showLabel?: boolean;
}

/**
 * Status indicator badge.
 */
export function StatusBadge({ status, showLabel = false }: StatusBadgeProps) {
  const statusConfig = {
    online: { color: 'bg-success', label: 'Online' },
    offline: { color: 'bg-gray-500', label: 'Offline' },
    away: { color: 'bg-amber-400', label: 'Away' },
    busy: { color: 'bg-danger', label: 'Busy' },
  };

  const { color, label } = statusConfig[status];

  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`w-2 h-2 rounded-full ${color}`} />
      {showLabel && <span className="text-sm text-gray-400">{label}</span>}
    </span>
  );
}
